import {
  EventingHelpers,
  useLocalEventing
} from '@app/common/src/hooks/useLocalEventing/useLocalEventing'
import { IIdleTimer, useIdleTimer } from 'react-idle-timer'
import { differenceInSeconds, isValid } from 'date-fns'
import { useAuthState } from '@dtx-company/inter-app/src/hooks/useAuthState'
import { useRouter } from 'next/router'

export interface ChangeTimestampEventProps {
  event?: Event
  prevTimestamp: Date
  timestamp: Date
}

interface InitTimestampEventProps {
  timestamp: Date
}

const TIME_LAST_ACTIVE_KEY = 'timeLastActive'

type IdleTimerEvent = (props: { event?: Event }) => void
type TimestampInitEvent = (props: InitTimestampEventProps) => void
type TimestampChangeEvent = (props: ChangeTimestampEventProps) => void

export type TimestampEvents = {
  onTimestampInit: TimestampInitEvent
  onTimestampChange: TimestampChangeEvent
  onAction: IdleTimerEvent
  onActive: IdleTimerEvent
  onPrompt: IdleTimerEvent
  onIdle: IdleTimerEvent
}

export interface TimeStampTrackingHelpers {
  getLastActiveTime: IIdleTimer['getLastActiveTime']
  addEventHandler: EventingHelpers<TimestampEvents>['addEventHandler']
  removeEventHandler: EventingHelpers<TimestampEvents>['removeEventHandler']
}

interface TimestampChangeDates {
  prevTimestamp?: Date
  timestamp: Date
}

const regExHoursMinutes = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/
const regExMonthDayYear = /(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])-([12]\d{0,3}$)/

/**
 * @param override A `string` value derived from the `wt_prev_time` query parameter.
 *
 * This `wt_prev_time` query parameter can be in the format of `MM-DD-YYYY`, `hh:mm` (base 24 time), or `MM-DD-YY_hh:mm`.
 * Examples: `04-20-1990`, `12:15`, or `12-25-2022_21:35`.
 *
 * Any deviation from this format will produce
 * no results. A valid entry will force the Welcome Toast to behave as though the last recorded user
 * visit took place at the date and/or time indicated in the query parameter. If only a time is
 * provided, the logic will assume the current date.
 *
 * This debug feature is only available to members of
 * the Flowcode org.
 * @returns a `Date` object or `undefined`
 */
export const parseQueryOverride = (override?: string | string[]): Date | undefined => {
  if (!override || Array.isArray(override)) return undefined
  let timeValue
  const [dateOrTime, hourAndMinutes] = override.split('_')

  if (!hourAndMinutes) {
    if (regExHoursMinutes.test(dateOrTime)) {
      const today = new Date().toLocaleDateString()
      timeValue = `${today}, ${dateOrTime}`
    } else if (regExMonthDayYear.test(dateOrTime)) {
      timeValue = dateOrTime
    }
  } else if (regExMonthDayYear.test(dateOrTime) && regExHoursMinutes.test(hourAndMinutes)) {
    timeValue = `${dateOrTime} ${hourAndMinutes}`
  }
  if (timeValue) return new Date(timeValue)
}

export function useTimestampTracking(): TimeStampTrackingHelpers {
  const { addEventHandler, removeEventHandler, triggerEvent } = useLocalEventing<TimestampEvents>()
  const router = useRouter()
  const queryOverride = router.query?.wt_prev_time

  const { isAuthenticated, jwt, isInFlowcodeOrg } = useAuthState()
  const localStorageItemKey = `${TIME_LAST_ACTIVE_KEY}-${jwt?.ithacaId}`

  const initTimestamp = (timestamp: Date): void => {
    if (!isAuthenticated) return

    localStorage.setItem(localStorageItemKey, timestamp.toISOString())

    triggerEvent('onTimestampInit', { timestamp })
  }

  const getTimestamp = (): TimestampChangeDates => {
    const overrideTimestamp = isInFlowcodeOrg && parseQueryOverride(queryOverride)

    const prevTimestamp = localStorage.getItem(localStorageItemKey)
    const timestamp = new Date()

    if (typeof prevTimestamp !== 'string') {
      initTimestamp(timestamp)
      return { timestamp }
    } else {
      const prevTimestampDate = new Date(prevTimestamp)
      if (!isValid(prevTimestampDate)) initTimestamp(timestamp)

      if (overrideTimestamp) {
        // Cleanup debug query params if any were present
        const { pathname, query } = router
        delete router.query.wt_prev_time
        router.replace({ pathname, query }, undefined, { shallow: true })
      }

      return {
        prevTimestamp: overrideTimestamp || prevTimestampDate, // prioritize the debug query date
        timestamp
      }
    }
  }

  const shouldChangeTimestamp = ({ prevTimestamp, timestamp }: TimestampChangeDates): boolean => {
    if (prevTimestamp && differenceInSeconds(timestamp, prevTimestamp) > 0) return true
    else return false
  }

  const changeTimestamp = (props: ChangeTimestampEventProps): void => {
    if (!isAuthenticated) return

    localStorage.setItem(localStorageItemKey, props.timestamp.toISOString())

    triggerEvent('onTimestampChange', props)
  }

  const onActive = (event?: Event): void => {
    triggerEvent('onActive', { event })

    const { prevTimestamp, timestamp } = getTimestamp()

    shouldChangeTimestamp({ prevTimestamp, timestamp }) &&
      changeTimestamp({ event, prevTimestamp: prevTimestamp as Date, timestamp })
  }

  const onAction = (event?: Event): void => {
    triggerEvent('onAction', { event })

    const { prevTimestamp, timestamp } = getTimestamp()

    shouldChangeTimestamp({ prevTimestamp, timestamp }) &&
      changeTimestamp({ event, prevTimestamp: prevTimestamp as Date, timestamp })
  }

  const { getLastActiveTime } = useIdleTimer({
    onActive,
    onAction,
    onPrompt: (event?: Event): void => triggerEvent('onPrompt', { event }),
    onIdle: (event?: Event): void => triggerEvent('onIdle', { event })
  })

  return {
    getLastActiveTime,
    addEventHandler,
    removeEventHandler
  }
}
